/**
 * The archive door on the web side: the question in front of archiving, the
 * write that undoes it, and the mark a project wears once it is through.
 *
 * One file because two screens need all three and neither owns them. The row
 * menu in the register archives a project the operator noticed while scanning;
 * the project page archives the one they opened on purpose. A copy of the
 * dialog in each would be two copies of the promise below, and that promise is
 * the only reason an operator trusts the button.
 *
 * ── WHY ARCHIVING ASKS AND UNARCHIVING DOES NOT ────────────────────────────
 *
 * `DeleteProjectDialog` next door reads the cascade counts before it will draw
 * a red button, because deleting a project destroys its tasks, milestones and
 * logged hours and there is no way back. Archiving is the opposite kind of act:
 * nothing is destroyed, and one click puts the project back where it was.
 *
 * So it would be easy to argue for no dialog at all - and that is the wrong
 * call, for a reason that has nothing to do with danger. The CONSEQUENCE of
 * archiving is invisible from the place the decision is made: the row vanishes
 * from the register and, more importantly, from every picker that offers a
 * project for new work, which is a set of screens the operator is not looking
 * at. `projects.archivedHint` is the sentence that says what that costs and
 * what it keeps, and a sentence nobody reads is a sentence that is not there -
 * so it goes in front of the click rather than in a tooltip beside it.
 *
 * The dialog therefore asks, but must NOT feel like the delete one. Its button
 * is brand, not red; it fetches no counts, because there is no hole to measure;
 * and its body is a promise about what stays rather than a warning about what
 * goes. Wearing the same red for a reversible step is how red stops meaning
 * anything.
 *
 * Unarchiving asks nothing. What it does is exactly what was requested and the
 * result is visible in the same breath - the project is back in the list. A
 * confirm there would be a dialog guarding an action whose entire effect is
 * "undo the last dialog".
 */
import { useState } from "react";
import { useT } from "../../../i18n/I18nProvider";
import { archiveProject, unarchiveProject, type Project } from "../../../lib/projects";
import { ConfirmModal } from "../_shared/ConfirmModal";
import { StatusPill, useToast } from "../../ui";

/**
 * The mark an archived project wears wherever it can still be reached.
 *
 * Muted rather than coloured: this is not a lifecycle STATUS - the project is
 * still `in_progress` or `completed` underneath, and the status pill beside
 * this one keeps saying so. Painting the archive in a status colour would make
 * two pills argue about the same fact.
 *
 * It is drawn at all because the register hides archived projects by default,
 * which means every place one DOES show up is a place somebody arrived at by
 * another route - a saved link, the client timeline, an archive view opened on
 * purpose. A record that is hidden from the list but looks identical when you
 * open it by link is a record whose state is invisible exactly when it matters.
 */
export function ArchivedBadge({ project }: { project: Project }) {
  const t = useT();
  if (!project.archivedAt) return null;
  return <StatusPill tone="mut">{t("projects.archivedBadge")}</StatusPill>;
}

/**
 * Archive this project, once the operator has read what that means.
 *
 * `onArchived` gets the saved row back rather than a bare signal: the endpoint
 * answers with the project, so a caller can swap the copy it is holding instead
 * of paying for a GET. The register uses it to refetch anyway - whether the row
 * should still be on screen depends on the archive filter the list is under,
 * and that is the list's question, not this dialog's.
 */
export function ArchiveProjectDialog({
  project,
  onClose,
  onArchived,
}: {
  project: Project;
  onClose: () => void;
  onArchived: (next: Project) => void;
}) {
  const t = useT();
  const toast = useToast();

  return (
    <ConfirmModal
      title={t("projects.archive")}
      // The promise, in full, at the moment of the decision. It is the only
      // copy in the app that says what archiving keeps, and the operator is
      // about to make the project hard to find on purpose.
      body={t("projects.archivedHint")}
      confirmLabel={t("projects.archive")}
      cancelLabel={t("common.cancel")}
      tone="brand"
      onConfirm={async () => {
        try {
          const next = await archiveProject(project.id);
          onArchived(next);
          toast(t("projects.archived"));
        } catch {
          // The dialog stays open on a failure. Closing it would leave the
          // operator looking at a register that still shows the row, with no
          // way to tell a refused write from one that worked and a list that
          // has not caught up.
          toast(t("common.error"));
        }
      }}
      onClose={onClose}
    />
  );
}

/**
 * Bring a project back, with no question in front of it.
 *
 * A hook rather than a component because there is nothing to render: the two
 * callers each already own a menu entry or a button, and what they are missing
 * is the write, the receipt and the busy flag that stops a double click from
 * firing two requests. (The server is idempotent, so a second one would be
 * harmless - but a button that can be pressed twice while the first press is in
 * flight looks broken whether or not it is.)
 */
export function useUnarchiveProject(): {
  busy: boolean;
  run: (project: Project, onDone: (next: Project) => void) => Promise<void>;
} {
  const t = useT();
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  return {
    busy,
    run: async (project, onDone) => {
      if (busy) return;
      setBusy(true);
      try {
        const next = await unarchiveProject(project.id);
        onDone(next);
        toast(t("projects.unarchived"));
      } catch {
        toast(t("common.error"));
      } finally {
        setBusy(false);
      }
    },
  };
}
